import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Popconfirm, theme, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { RootState } from 'store/configureStore';
import { managementOp } from 'store/ducks/Management';
import { useUnsavedChanges } from 'utils/useUnsavedChanges';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import { ICategory } from 'store/ducks/Management/types';
import ApiService from 'utils/ApiCalls';
import CustomTable from 'components/Tables/CustomTable';
import DataManagementLayout from 'components/Layouts/DataManagementLayout/DataManagementLayout';
import * as Styled from './CategoryManagement.styled';

const { useToken } = theme;

const CategoryManagement: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { token } = useToken();
  const location = useLocation();

  const dispatch = useDispatch();
  const { drawTable, lang, category, saveClicked } = useSelector(
    (state: RootState) => ({
      drawTable: state.management.drawTable,
      lang: state.global.lang,
      category: state.management.category,
      saveClicked: state.management.saveClicked
    })
  );

  const [rows, setRows] = useState<ICategory[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [modifiedRowIds, setModifiedRowIds] = useState<Set<number>>(new Set());
  const [initialRows, setInitialRows] = useState<ICategory[]>([]);
  const hasUnsavedChanges = useMemo(() => {
    if (initialRows.length === 0 && rows.length === 0) {
      return false;
    }
    const rowsChanged = JSON.stringify(rows) !== JSON.stringify(initialRows);
    const hasChangesSet = hasChanges;
    const hasModifiedRows = modifiedRowIds.size > 0;
    return rowsChanged || hasChangesSet || hasModifiedRows;
  }, [rows, initialRows, hasChanges, modifiedRowIds]);

  useUnsavedChanges({ hasChanges: hasUnsavedChanges });

  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  const handleFieldChange = (
    rowKey: string,
    field: keyof ICategory,
    value: string
  ) => {
    setRows((prevRows) => {
      const updatedRows = prevRows.map((row) =>
        row.key === rowKey ? { ...row, [field]: value } : row
      );

      // Track modified existing rows by cat_id
      const updatedRow = updatedRows.find((row) => row.key === rowKey);
      if (updatedRow?.cat_id) {
        setModifiedRowIds((prev) => new Set(prev).add(updatedRow.cat_id!));
      }

      return updatedRows;
    });
    setHasChanges(true);
    dispatch(managementOp.setDataEdited(true));
  };

  useEffect(() => {
    if (category && Array.isArray(category)) {
      // Sort categories by id to maintain consistent order
      const sortedCategories = [...category].sort(
        (a, b) => (a.id || 0) - (b.id || 0)
      );

      const mappedRows = sortedCategories.map(
        (
          cat: { id?: number; short_name: string; description: string },
          index: number
        ) => ({
          key: String(index + 1),
          cat_id: cat.id, // Backend sends 'id', map to 'cat_id'
          short_name: cat.short_name,
          description: cat.description
        })
      );

      setRows(mappedRows);

      // Store original data for comparison
      const originalMap = new Map();
      mappedRows.forEach((row) => {
        if (row.cat_id) {
          originalMap.set(row.cat_id, { ...row });
        }
      });

      setModifiedRowIds(new Set());
      setHasChanges(false);

      if (mappedRows.length > 0) {
        setInitialRows(mappedRows);
      }
    }
  }, [category, lang]);

  // Enable/disable save based on changes
  useEffect(() => {
    dispatch(managementOp.setDataEdited(hasChanges));
  }, [hasChanges, dispatch]);

  const handleSave = async () => {
    try {
      const modifiedRows = rows
        .filter((row) => {
          const isNewRow = !row.cat_id;
          const isModifiedRow = row.cat_id && modifiedRowIds.has(row.cat_id);
          return isNewRow || isModifiedRow;
        })
        .filter((row) => row.short_name.trim() || row.description.trim())
        .map((row) => {
          if (row.cat_id) {
            return {
              cat_id: row.cat_id,
              short_name: row.short_name,
              description: row.description
            };
          } else {
            return {
              cat_id: generateUniqueId(),
              short_name: row.short_name,
              description: row.description
            };
          }
        });

      if (modifiedRows.length === 0) {
        message.info('No changes to save');
        return;
      }

      await ApiService.saveCategory({ categories: modifiedRows }, lang);

      const refreshedData = await ApiService.getCategory(lang, {});
      if (refreshedData?.data) {
        dispatch(managementOp.setCategory(refreshedData.data));
      }

      setHasChanges(false);
      setModifiedRowIds(new Set());
      dispatch(managementOp.setDataEdited(false));
      setInitialRows(rows);

      message.success('Categories saved successfully!');
    } catch (error) {
      console.error('Save failed:', error);
      message.error('Failed to save categories');
    }
  };

  useEffect(() => {
    if (saveClicked) {
      handleSave();
      dispatch(managementOp.setSaveClicked(false));
    }
  }, [saveClicked]);

  const handleAddRow = () => {
    const newRow: ICategory = {
      key: `new_${Date.now()}`,
      short_name: '',
      description: ''
    };
    setRows((prevRows) => [...prevRows, newRow]);
    setHasChanges(true);
    dispatch(managementOp.setDataEdited(true));
  };

  const handleDeleteClick = async (catId: number) => {
    try {
      await ApiService.deleteCategory(catId);

      const refreshedData = await ApiService.getCategory(lang, {});
      if (refreshedData?.data) {
        dispatch(managementOp.setCategory(refreshedData.data));
      }

      message.success('Category deleted successfully!');
    } catch (error) {
      console.error('Delete failed:', error);
      message.error('Failed to delete category');
    }
  };

  const cancel = (e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
    message.error(t('CATEGORY_NOT_DELETED'));
  };

  const columns: ColumnsType<ICategory> = useMemo(
    () => [
      {
        title: t('ACTION'),
        key: 'action',
        width: 100,
        render: (_, record) => (
          <Styled.StyledButtonSection token={token}>
            <Popconfirm
              title={t('DELETE_CATEGORY')}
              description={t('CONFIRM_DELETE_CATEGORY')}
              onConfirm={() =>
                record.cat_id && handleDeleteClick(record.cat_id)
              }
              onCancel={cancel}
              okText={t('YES')}
              cancelText={t('NO')}
              disabled={!record.cat_id}
            >
              <Styled.StyledButton
                type='primary'
                size='small'
                shape='circle'
                icon={<DeleteOutlined />}
                disabled={!record.cat_id}
              />
            </Popconfirm>
          </Styled.StyledButtonSection>
        )
      },
      {
        title: t('SHORT_NAME'),
        dataIndex: 'short_name',
        key: 'short_name',
        width: 425,
        render: (val, data) => (
          <Styled.ColumnTextBox
            width={500}
            value={data.short_name}
            placeholder='Enter short name'
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              handleFieldChange(data.key!, 'short_name', e.target.value)
            }
          />
        )
      },
      {
        title: t('DESCRIPTION'),
        dataIndex: 'description',
        key: 'description',
        width: 425,
        render: (val, data) => (
          <Styled.ColumnTextBox
            width={425}
            value={data.description}
            placeholder='Enter description'
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              handleFieldChange(data.key!, 'description', e.target.value)
            }
          />
        )
      }
    ],
    [t, token]
  );

  return (
    <DataManagementLayout>
      <Styled.ContentWrapper>
        {drawTable ? (
          <>
            <CustomTable
              drawTable={drawTable}
              columns={columns}
              dataSource={rows}
              itemsCount={rows.length}
              pagination={false}
              scroll={{ x: 1000 }}
            />
            <Styled.AddButton
              type='primary'
              onClick={handleAddRow}
              icon={<PlusOutlined />}
              shape='circle'
            />
          </>
        ) : (
          <CustomTable
            drawTable={drawTable}
            itemsCount={0}
            columns={columns}
            dataSource={[]}
            pagination={false}
          />
        )}
      </Styled.ContentWrapper>
    </DataManagementLayout>
  );
};

export default CategoryManagement;
